/**
 * Instrumentação do status default de um livro criado sem escolha do usuário.
 *
 * Contexto: adicionar um livro a uma LISTA cria o livro na estante mesmo sem o
 * usuário tocar no seletor de status. O valor escolhido é um palpite do
 * produto, não uma decisão dele — então precisamos saber se o palpite está
 * certo: quantos desses livros o usuário depois move para outro status, e para
 * qual.
 *
 * ⚠️ O default MUDOU quando posse virou eixo próprio. Era `owned`, que naquele
 * modelo significava só "na estante, sem pressa"; hoje `status: "owned"` é uma
 * AFIRMAÇÃO DE POSSE (o invariante força `ownership` não-nulo), e mantê-lo
 * como default diria ao visitante do perfil "esta pessoa já tem este livro"
 * sem ela nunca ter dito isso — exatamente a mentira que o caso do presente
 * não pode contar. O default passou a ser `want_to_read`, que expressa
 * interesse sem reivindicar exemplar nenhum.
 *
 * Implementação client-only e best-effort: guarda os ids de `user_books`
 * criados por esse caminho em localStorage e, na PRIMEIRA troca de status
 * daquele doc, emite o evento e esquece o id (a pergunta é "o default pegou?",
 * não o histórico completo). Sem storage → não instrumenta; nunca lança.
 */
import { track } from "./telemetry";

const KEY = "livra_default_status_books";
/** Teto de ids guardados — a janela de interesse é curta, não é histórico. */
const MAX_TRACKED = 50;

function read(): string[] {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function write(ids: string[]): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(ids.slice(0, MAX_TRACKED)));
  } catch {
    /* storage indisponível — instrumentação nunca bloqueia o fluxo */
  }
}

/** Marca um `user_books.id` que nasceu com o status default (não por escolha). */
export function markDefaultedStatus(userBookId: string): void {
  if (!userBookId) return;
  write([userBookId, ...read().filter((id) => id !== userBookId)]);
}

/**
 * Chamado em toda troca de status. Se o doc tinha nascido com o default,
 * emite `shelf_default_status_changed` e para de rastreá-lo.
 */
export function noteStatusChange(userBookId: string, status: string): void {
  if (!userBookId) return;
  const ids = read();
  if (!ids.includes(userBookId)) return;
  write(ids.filter((id) => id !== userBookId));
  track("shelf_default_status_changed", { to: status });
}
